import Menu from './menu';

import Util from '../utils/util';
import { attachRichTooltip } from '../utils/tooltip';

import { Modules } from '@kaetram/common/network';

import type ItemInfo from './iteminfo';
import type { Bonuses, Enchantments, Stats } from '@kaetram/common/types/item';
import type SpritesController from '../controllers/sprites';
import type Player from '../entity/character/player/player';

function escapeHtml(s: string): string {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

const ENCHANTMENT_NAMES_ES: { [key: number]: string } = {
        [Modules.Enchantment.Bloodsucking]: 'Vampírico',
        [Modules.Enchantment.Critical]: 'Crítico',
        [Modules.Enchantment.Evasion]: 'Evasión',
        [Modules.Enchantment.Thorns]: 'Espinas',
        [Modules.Enchantment.Explosive]: 'Explosivo',
        [Modules.Enchantment.Stun]: 'Aturdir',
        [Modules.Enchantment.AntiStun]: 'Anti-Aturdir',
        [Modules.Enchantment.Splash]: 'Salpicadura',
        [Modules.Enchantment.DoubleEdged]: 'Doble Filo',
        [Modules.Enchantment.Bleeding]: 'Sangrado'
    },
    STAT_LABELS_ES: { [key: string]: string } = {
        crush: 'Contundente',
        slash: 'Cortante',
        stab: 'Perforante',
        archery: 'Arquería',
        magic: 'Magia'
    },
    BONUS_LABELS_ES: { [key: string]: string } = {
        accuracy: 'Precisión',
        strength: 'Fuerza',
        archery: 'Arquería',
        magic: 'Magia'
    };

type UnequipCallback = (type: Modules.Equipment) => void;

export default class Equipments extends Menu {
    // Player image elements
    private playerArmour: HTMLElement = document.querySelector('#player-image-armour')!;
    private playerWeapon: HTMLElement = document.querySelector('#player-image-weapon')!;

    // Equipment slots elements
    private weapon: HTMLElement = document.querySelector('.equip-weapon-slot')!;
    private weaponSkin: HTMLElement = document.querySelector('.equip-weapon-skin-slot')!;
    private armour: HTMLElement = document.querySelector('.equip-armour-slot')!;
    private armourSkin: HTMLElement = document.querySelector('.equip-armour-skin-slot')!;
    private pendant: HTMLElement = document.querySelector('.equip-pendant-slot')!;
    private ring: HTMLElement = document.querySelector('.equip-ring-slot')!;
    private boots: HTMLElement = document.querySelector('.equip-boots-slot')!;
    private arrow: HTMLElement = document.querySelector('.equip-arrows-slot')!;
    private wings: HTMLElement = document.querySelector('.equip-wings-slot')!;

    // Counts
    private arrowsCount: HTMLElement = document.querySelector('#arrows-count')!;

    // Navigation elements
    private previous: HTMLElement = document.querySelector('#player-image-navigator > .previous')!;
    private next: HTMLElement = document.querySelector('#player-image-navigator > .next')!;

    // Stats elements
    private attackStats: HTMLElement = document.querySelector('#attack-stats')!;
    private defenseStats: HTMLElement = document.querySelector('#defense-stats')!;
    private bonuses: HTMLElement = document.querySelector('#bonuses')!;
    private enchantmentsPanel: HTMLElement = document.querySelector('#enchantments-stats')!;

    // Class properties
    private imageOrientation: Modules.Orientation = Modules.Orientation.Down;

    private unequipCallback?: UnequipCallback;

    public constructor(
        private player: Player,
        private sprites: SpritesController,
        private itemInfo?: ItemInfo
    ) {
        super('#equipments', '#close-equipments', '#equipment-button');

        // Navigation event listeners.
        this.previous.addEventListener('click', () => this.handleNavigation('previous'));
        this.next.addEventListener('click', () => this.handleNavigation('next'));

        // Equipment slot event listeners -- definitely not stolen from the state page :)
        this.weapon.addEventListener('click', () =>
            this.unequipCallback?.(Modules.Equipment.Weapon)
        );
        this.weaponSkin.addEventListener('click', () =>
            this.unequipCallback?.(Modules.Equipment.WeaponSkin)
        );
        this.armour.addEventListener('click', () =>
            this.unequipCallback?.(Modules.Equipment.Armour)
        );
        this.armourSkin.addEventListener('click', () =>
            this.unequipCallback?.(Modules.Equipment.ArmourSkin)
        );
        this.pendant.addEventListener('click', () =>
            this.unequipCallback?.(Modules.Equipment.Pendant)
        );
        this.ring.addEventListener('click', () => this.unequipCallback?.(Modules.Equipment.Ring));
        this.boots.addEventListener('click', () => this.unequipCallback?.(Modules.Equipment.Boots));
        this.arrow.addEventListener('click', () =>
            this.unequipCallback?.(Modules.Equipment.Arrows)
        );
        this.wings.addEventListener('click', () => this.unequipCallback?.(Modules.Equipment.Wings));

        // Right-click to inspect equipment
        let equipSlots: [HTMLElement, Modules.Equipment][] = [
            [this.weapon, Modules.Equipment.Weapon],
            [this.weaponSkin, Modules.Equipment.WeaponSkin],
            [this.armour, Modules.Equipment.Armour],
            [this.armourSkin, Modules.Equipment.ArmourSkin],
            [this.pendant, Modules.Equipment.Pendant],
            [this.ring, Modules.Equipment.Ring],
            [this.boots, Modules.Equipment.Boots],
            [this.arrow, Modules.Equipment.Arrows],
            [this.wings, Modules.Equipment.Wings]
        ];

        for (let [el, type] of equipSlots) {
            el.addEventListener('contextmenu', (e) => {
                e.preventDefault();
                e.stopPropagation();
                let equip = this.player.equipments[type];
                if (equip?.exists())
                    this.itemInfo?.show(
                        {
                            key: equip.key,
                            name: equip.name,
                            count: equip.count > 1 ? equip.count : undefined,
                            equipmentType: type,
                            attackStats: equip.attackStats,
                            defenseStats: equip.defenseStats,
                            bonuses: equip.bonuses,
                            enchantments: equip.enchantments,
                            passiveAbility: equip.passiveAbility || undefined,
                            movementModifier:
                                equip.movementModifier > 0 ? equip.movementModifier : undefined
                        },
                        e.clientX,
                        e.clientY
                    );
            });

            attachRichTooltip(el, () => this.buildEquipTooltip(type));
        }
    }

    /**
     * Synchronizes the equipment screen with the player's equipment data.
     */

    public override synchronize(): void {
        this.loadStats();
        this.loadPlayerImage();

        // Synchronize equipment data
        this.weapon.style.backgroundImage = Util.getImageURL(this.player.getWeapon().key);
        this.weaponSkin.style.backgroundImage = Util.getImageURL(this.player.getWeaponSkin().key);
        // Cloth armour shouldn't be displayed in the UI.
        this.armour.style.backgroundImage = Util.getImageURL(
            this.player.getArmour().key === 'clotharmor' ? '' : this.player.getArmour().key
        );
        this.armourSkin.style.backgroundImage = Util.getImageURL(this.player.getArmourSkin().key);
        this.pendant.style.backgroundImage = Util.getImageURL(this.player.getPendant().key);
        this.ring.style.backgroundImage = Util.getImageURL(this.player.getRing().key);
        this.boots.style.backgroundImage = Util.getImageURL(this.player.getBoots().key);
        this.arrow.style.backgroundImage = Util.getImageURL(this.player.getArrows().key);
        this.wings.style.backgroundImage = Util.getImageURL(this.player.getWings().key);

        // Synchronize arrow count
        let arrowCount = this.player.getArrows().count;

        this.arrowsCount.innerHTML = arrowCount < 1 ? '' : `+${arrowCount}`;
    }

    /**
     * Reload the image every time we resize the screen.
     */

    public override resize(): void {
        this.loadPlayerImage();
    }

    /**
     * Handles the navigation arrows. We just change the orientation and then update the player image.
     */

    private handleNavigation(direction: 'next' | 'previous'): void {
        let orientations = [
                Modules.Orientation.Left,
                Modules.Orientation.Up,
                Modules.Orientation.Right,
                Modules.Orientation.Down
            ],
            index = orientations.indexOf(this.imageOrientation);

        switch (direction) {
            case 'previous': {
                index = index === 0 ? orientations.length - 1 : index - 1;
                break;
            }

            case 'next': {
                index = index === orientations.length - 1 ? 0 : index + 1;
                break;
            }
        }

        this.imageOrientation = orientations[index];

        this.loadPlayerImage();
    }

    /**
     * Adds up all the attack, defense, and bonuses from all the equipments and displays them.
     */

    private loadStats(): void {
        let attackStats: Stats = Util.getEmptyStats(),
            defenseStats: Stats = Util.getEmptyStats(),
            bonuses: Bonuses = Util.getEmptyBonuses();

        // iterate through all the equipments and add up the stats.
        for (let [type, equipment] of Object.entries(this.player.equipments)) {
            if (!equipment.exists()) continue;

            let eqType = Number(type),
                isWeapon =
                    eqType === Modules.Equipment.Weapon || eqType === Modules.Equipment.WeaponSkin;

            attackStats.crush += equipment.attackStats.crush;
            attackStats.slash += equipment.attackStats.slash;
            attackStats.stab += equipment.attackStats.stab;
            attackStats.archery += equipment.attackStats.archery;
            attackStats.magic += equipment.attackStats.magic;

            if (!isWeapon) {
                defenseStats.crush += equipment.defenseStats.crush;
                defenseStats.slash += equipment.defenseStats.slash;
                defenseStats.stab += equipment.defenseStats.stab;
                defenseStats.archery += equipment.defenseStats.archery;
                defenseStats.magic += equipment.defenseStats.magic;
            }

            bonuses.accuracy += equipment.bonuses.accuracy;
            bonuses.strength += equipment.bonuses.strength;
            bonuses.archery += equipment.bonuses.archery;
            bonuses.magic += equipment.bonuses.magic;
        }

        let stats = ['Crush', 'Slash', 'Stab', 'Archery', 'Magic'],
            statLabels: { [key: string]: string } = {
                Crush: 'Contundente',
                Slash: 'Cortante',
                Stab: 'Perforante',
                Archery: 'Arquería',
                Magic: 'Magia'
            },
            statBonuses = ['Accuracy', 'Strength', 'Archery', 'Magic'],
            bonusLabels: { [key: string]: string } = {
                Accuracy: 'Precisión',
                Strength: 'Fuerza',
                Archery: 'Arquería',
                Magic: 'Magia'
            },
            hasDefense =
                defenseStats.crush > 0 ||
                defenseStats.slash > 0 ||
                defenseStats.stab > 0 ||
                defenseStats.archery > 0 ||
                defenseStats.magic > 0;

        this.defenseStats.style.display = hasDefense ? '' : 'none';

        for (let stat of stats) {
            let lStat = stat.toLowerCase(),
                attackElement = this.attackStats.querySelector(`.${lStat.toLowerCase()}`)!,
                defenseElement = this.defenseStats.querySelector(`.${lStat.toLowerCase()}`)!,
                attackStat = attackStats[lStat as keyof Stats],
                defenseStat = defenseStats[lStat as keyof Stats];

            attackElement.textContent = `${statLabels[stat]}: ${
                attackStat > 0 ? '+' : ''
            }${attackStat}`;
            defenseElement.textContent = `${statLabels[stat]}: ${
                defenseStat > 0 ? '+' : ''
            }${defenseStat}`;
        }

        for (let bonus of statBonuses) {
            let lBonus = bonus.toLowerCase(),
                bonusElement = this.bonuses.querySelector(`.${lBonus.toLowerCase()}`)!,
                bonusStat = bonuses[lBonus as keyof Bonuses];

            bonusElement.textContent = `${bonusLabels[bonus]}: ${
                bonusStat > 0 ? '+' : ''
            }${bonusStat}`;
        }

        // Collect enchantments from all equipped items
        let merged: { [id: number]: { name: string; level: number } } = {};

        for (let equipment of Object.values(this.player.equipments)) {
            if (!equipment.exists() || !equipment.enchantments) continue;

            for (let [id, data] of Object.entries(equipment.enchantments)) {
                let numId = Number(id),
                    level = (data as { level: number })?.level ?? 0;
                if (level <= 0) continue;

                let name = ENCHANTMENT_NAMES_ES[numId] ?? `Enc. ${numId}`;
                if (merged[numId]) merged[numId].level += level;
                else merged[numId] = { name, level };
            }
        }

        // Clear old entries, keep the title + br
        let enchEntries = this.enchantmentsPanel.querySelectorAll('.enchant-entry');
        for (let e of enchEntries) e.remove();

        let ids = Object.keys(merged);
        if (ids.length > 0) {
            this.enchantmentsPanel.style.display = '';
            this.enchantmentsPanel.classList.toggle('enchant-top', !hasDefense);
            for (let id of ids) {
                let entry = merged[Number(id)],
                    p = document.createElement('p');
                p.className = 'enchant-entry';
                p.textContent = `${entry.name}: ${entry.level}`;
                this.enchantmentsPanel.append(p);
            }
        } else this.enchantmentsPanel.style.display = 'none';
    }

    /**
     * Prepares the player image that is displayed in the equipment screen. We pick both the
     * armour and the weapon and overlay them on top of each other.
     */

    private loadPlayerImage(): void {
        let armourUrl = `url("/img/sprites/player/${this.player.getArmour().key}.png")`,
            armourSprite = this.player.sprite,
            isFlipped = this.imageOrientation === Modules.Orientation.Left,
            index = this.getOrientationIndex();

        if (!armourSprite) return;

        // Set the player armour sprite.
        this.playerArmour.style.backgroundImage = armourUrl;
        this.playerArmour.style.backgroundPosition = `0 -${index * armourSprite.height}px`;

        /**
         * If the player has a weapon we do some fancy math, otherwise we remove the weapon
         * element's background image.
         */

        if (this.player.hasWeapon()) {
            let weaponKey = this.player.getWeapon().key,
                weaponSprite = this.sprites.get(`player/${weaponKey}`);

            if (weaponSprite) {
                let weaponUrl = `url("/img/sprites/player/${weaponKey}.png")`,
                    offsetX = Math.floor(weaponSprite.offsetX / 2),
                    offsetY = Math.floor(weaponSprite.offsetY / 2);

                this.playerWeapon.style.width = `${weaponSprite.width}px`;
                this.playerWeapon.style.height = `${weaponSprite.height}px`;
                this.playerWeapon.style.marginLeft = `${offsetX}px`;
                this.playerWeapon.style.marginTop = `${offsetY}px`;
                this.playerWeapon.style.backgroundImage = weaponUrl;
                this.playerWeapon.style.backgroundPosition = `0 -${index * weaponSprite.height}px`;
            } else this.playerWeapon.style.backgroundImage = 'none';
        } else this.playerWeapon.style.backgroundImage = 'none';

        // Flip the player image if we are rendering the right orientation.
        this.playerArmour.style.transform = `scaleX(${isFlipped ? -1 : 1})`;
        this.playerWeapon.style.transform = `scaleX(${isFlipped ? -1 : 1})`;
    }

    /**
     * Orientation grabs the y frame index of the sprite in its sprite sheet. The left and right
     * orientations are the same since we just flip the sprite.
     * @returns A number representing the y frame index of the sprite in its sprite sheet.
     */

    private getOrientationIndex(): number {
        switch (this.imageOrientation) {
            case Modules.Orientation.Left:
            case Modules.Orientation.Right: {
                return 1;
            }

            case Modules.Orientation.Up: {
                return 4;
            }

            case Modules.Orientation.Down: {
                return 7;
            }
        }
    }

    private buildEquipTooltip(type: Modules.Equipment): string {
        let equip = this.player.equipments[type];
        if (!equip?.exists()) return '';

        let parts: string[] = [];

        parts.push(`<div class="tooltip-title">${escapeHtml(equip.name)}</div>`);

        let isWeapon =
                type === Modules.Equipment.Weapon ||
                type === Modules.Equipment.WeaponSkin ||
                type === Modules.Equipment.Arrows,
            isDefensive =
                type === Modules.Equipment.Armour ||
                type === Modules.Equipment.ArmourSkin ||
                type === Modules.Equipment.Boots;

        if (!isDefensive) {
            let atk = this.formatStatsHtml(equip.attackStats, STAT_LABELS_ES);
            if (atk) parts.push(`<div class="tooltip-section"><b>Ataque</b>${atk}</div>`);
        }

        if (!isWeapon) {
            let def = this.formatStatsHtml(equip.defenseStats, STAT_LABELS_ES);
            if (def) parts.push(`<div class="tooltip-section"><b>Defensa</b>${def}</div>`);
        }

        if (isWeapon) {
            let enchHtml = this.formatEnchantmentsHtml(equip.enchantments);
            if (enchHtml)
                parts.push(
                    `<div class="tooltip-section tooltip-enchant"><b>Encantamientos</b>${enchHtml}</div>`
                );
        }

        let bon = this.formatStatsHtml(equip.bonuses, BONUS_LABELS_ES);
        if (bon) parts.push(`<div class="tooltip-section"><b>Bonos</b>${bon}</div>`);

        if (!isWeapon) {
            let enchHtml = this.formatEnchantmentsHtml(equip.enchantments);
            if (enchHtml)
                parts.push(
                    `<div class="tooltip-section tooltip-enchant"><b>Encantamientos</b>${enchHtml}</div>`
                );
        }

        return parts.join('');
    }

    private formatStatsHtml(
        stats: Stats | Bonuses | undefined,
        labels: { [key: string]: string }
    ): string {
        if (!stats) return '';
        let out = '';
        for (let [key, value] of Object.entries(stats as unknown as { [key: string]: number })) {
            let n = Number(value);
            if (!Number.isFinite(n) || n === 0) continue;
            let label = labels[key] ?? key,
                sign = n > 0 ? '+' : '';
            out += `<div>${escapeHtml(label)}: ${sign}${n}</div>`;
        }
        return out;
    }

    private formatEnchantmentsHtml(enchantments?: Enchantments): string {
        if (!enchantments || Object.keys(enchantments).length === 0) return '';
        let out = '';
        for (let [id, data] of Object.entries(enchantments)) {
            let name = ENCHANTMENT_NAMES_ES[Number(id)] ?? `Enc.${id}`,
                level = (data as { level: number })?.level ?? 0;
            if (level > 0) out += `<div>· ${escapeHtml(name)} Nv. ${level}</div>`;
        }
        return out;
    }

    /**
     * Callback for when we click on an equipment slot.
     * @param callback Contains the slot type we are selecting.
     */

    public onUnequip(callback: UnequipCallback): void {
        this.unequipCallback = callback;
    }
}
